import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import React from "react";
import { SectionCard, SectionCardTitle } from "@/components";
import ProductsImageGrid from "../ImageGrid";

// ImageWithLoader component for showing a spinner while loading images
interface ImageWithLoaderProps {
  src: string;
  alt?: string;
  className?: string;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  src,
  alt,
  className,
}) => {
  const [loading, setLoading] = React.useState(true);
  return (
    <div className="relative w-full flex justify-center items-center min-h-[8rem]">
      {loading && (
        <span className="absolute z-10 flex items-center justify-center w-full h-full">
          <svg
            className="animate-spin h-8 w-8 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </span>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        style={loading ? { visibility: "hidden" } : {}}
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    </div>
  );
};

export default function ProductDetailsPage() {
  const { nameId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState<any>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const formatTrademark = (text: string): string => {
    if (!text) return "";
    return text
      .replace(/\b(Vibrio)\b/gi, "<i>$1</i>")
      .replace(/®/g, "<sup>®</sup>")
      .replace(/™/g, "<sup>™</sup>")
      .replace(/\(R\)/gi, "<sup>®</sup>")
      .replace(/\(TM\)/gi, "<sup>™</sup>");
  };

  useEffect(() => {
    fetch("/data/products.json")
      .then(res => res.json())
      .then(data => {
        const found = data.products.find((p: any) => p.nameId === nameId);
        setProduct(found || null);
      });
  }, [nameId]);


  const images = [
  product?.image,
  product?.image2,
  product?.image3,
  product?.image4,
  product?.image5,
  product?.image6,
  product?.image7,
].filter(Boolean);

// Auto-slide
useEffect(() => {
  if (!images.length) return;
  const interval = setInterval(
    () => setCarouselIndex(i => (i + 1) % images.length),
    3000
  );
  return () => clearInterval(interval);
}, [images]);

  return (
  <>
    <SectionCard className="px-4 lg:px-[90px] mx-0 h-auto">
      <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
        <div className="lg:w-1/2 w-full lg:mt-20">
          <SectionCardTitle>Our Solutions</SectionCardTitle>
          <p className="text-[14px] lg:text-[16px] leading-[24px] lg:leading-[28px]     text-[#415667] mt-4">
            Discover our diverse range of science-driven probiotic and biotech solutions for aquaculture, poultry, shrimp, fish, ruminants, and more.
          </p>
        </div>
        <div className="lg:w-2/3 w-full flex justify-start lg:justify-end">
        <div className="w-fit">
          <ProductsImageGrid />
        </div>
        </div>

      </div>
    </SectionCard> 
    <section className="bg-gray-50 pt-5 pb-50 min-h-screen">
      <div className="container mx-auto px-4">

    {!product ? (
        <p className="p-6">Loading…</p>
      ) : (
        <>
        <button
          onClick={() => navigate(-1)}
          className="mb-4 cursor-pointer underline text-gray-700"
        >
          ← Back
        </button>

        <div className=" w-11/12 lg:w-3/4  mx-auto ">
          <div className="sticky top-0 flex items-center justify-between p-3 z-10 border-b">
            {product.imglogo && (
              <img
                src={product.imglogo}
                alt={product.name}
                className={`object-contain ${
                  ["Snail", "Ruminant"].includes(product.brand)
                    ? "h-25"
                    : "h-20"
                }`}
              />
            )}

            {/* Close replaced with Back */}
           
          </div>

          {/* ⬇️ SAME CONTENT AS MODAL */}
          <div className="p-6 grid grid-cols-1 lg:grid-cols-10 gap-6">

            {/* LEFT CONTENT */}
            <div className="lg:col-span-7 space-y-4">

              <p className="text-sm text-gray-600">
                <span className="font-semibold">Animal Category :</span>{" "}
                {product.brand}
              </p>

              {product.id === 16 && (
                <span className="text-gray-700 font-semibold mb-3 block">
                  Key Ingredients:{" "}
                  <span className="font-normal">
                    {product.key_ingredients}
                  </span>
                </span>
              )}

              {/* Benefits */}
              <div
                className="bg-blue-50 p-4 rounded-lg text-justify"
                style={{
                  background:
                    "linear-gradient(309.45deg, rgba(0, 167, 89, 0.08) 24.42%, rgba(0, 163, 212, 0.08) 69.56%)",
                }}
              >
                <h6 className="font-semibold mb-2">Benefits</h6>

                <ul className="list-disc pl-5 space-y-1">
                  {product.benefits &&
                    product.benefits
                      .flatMap((b: string) => b.split("_x000D_\n"))
                      .map((line: string, i: number) => (
                        <li
                          key={i}
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{
                            __html: formatTrademark(line.trim()),
                          }}
                        />
                      ))}
                </ul>
              </div>

              {/* Dosage */}
              <div>
                <h6 className="font-semibold text-justify">
                  Dosage & Usage :
                </h6>
                <p
                  className="mt-5 text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: formatTrademark(
                      product.useage_dosage?.replace(
                        /_x000D_\n?/g,
                        "<br />"
                      ) || ""
                    ),
                  }}
                />
              </div>

              {/* Presentation */}
              <div>
                <h6 className="font-semibold text-justify">
                  Presentation:
                </h6>
                <p
                  className="mt-4 text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: formatTrademark(product.presentation || ""),
                  }}
                />
              </div>

              {product.storage &&
                product.storage !== product.dosage && (
                  <div>
                    <h6 className="font-semibold">Storage:</h6>
                    <p
                      className="text-gray-700"
                      dangerouslySetInnerHTML={{
                        __html: formatTrademark(product.storage || ""),
                      }}
                    />
                  </div>
                )}
            </div>

            {/* RIGHT — IMAGE CAROUSEL (same as modal) */}
            <div className="lg:col-span-3 flex flex-col justify-start mt-10 items-center w-full">
              {images.length > 0 && (
                <div className="relative w-full flex flex-col items-center">
                  <div className="overflow-hidden w-full flex justify-center">
                    <ImageWithLoader
                      src={images[carouselIndex]}
                      alt={product.name}
                      className="max-h-100 object-contain transition-all duration-700 ease-in-out"
                    />
                  </div>

                  {images.length > 1 && (
                    <div className="flex justify-center mt-3 space-x-2">
                      {images.map((_, idx) => (
                        <span
                          key={idx}
                          onClick={() => setCarouselIndex(idx)}
                          className={`block cursor-pointer w-[15px] h-[15px] rounded-full ${
                            carouselIndex === idx
                              ? "bg-primary"
                              : "bg-gray-400"
                          }`}
                        ></span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
          </div>
          
        </div>
        </>
        )}
      </div>
    </section>
    </>
  );
}
